import { z } from "zod";

// `makeRaceId` (the deterministic id generator) lives in a separate file
// because it imports `node:crypto`. Keeping it out of this module lets
// browser consumers import `RaceSchema` without pulling Node crypto into
// the bundle. Server/script consumers import from `@runner/shared/src/make-race-id`.

export const RaceStatusSchema = z.enum(["open", "upcoming", "closed", "completed"]);
export type RaceStatus = z.infer<typeof RaceStatusSchema>;

// Numeric distances: UPPERCASE (5K, 10K, 32K) — category A
// Word distances:    lowercase (half, full, ultra, trail) — category B
// "other" is a future-compat catch-all.
export const DistanceSchema = z.enum(["5K", "10K", "32K", "half", "full", "ultra", "trail", "other"]);
export type Distance = z.infer<typeof DistanceSchema>;

// Alias kept for web code that still uses DistanceType.
export type DistanceType = Distance;

// Legacy nested coordinate shape — kept for korea.json fallback path only.
export const CoordinateSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});
export type Coordinate = z.infer<typeof CoordinateSchema>;

// Per-distance entry fee — UI display only (not persisted in D1 Phase 1).
export const FeeSchema = z.object({
  distance: z.string(),
  amount: z.number(),
  currency: z.string(),
});
export type Fee = z.infer<typeof FeeSchema>;

// Registration window — UI display + D-day derivation (not persisted in D1 Phase 1).
export const RegistrationPeriodSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});
export type RegistrationPeriod = z.infer<typeof RegistrationPeriodSchema>;

// Phase 6 글로벌 확장 — ISO 코드 strict 검증.
// Phase 1 KR/KRW 데이터는 모두 통과 (fixtures: research/data/marathons/races_webapp.json).
const COUNTRY_CODE = z.string().regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2 (uppercase, 2 chars)");
const CURRENCY_CODE = z.string().regex(/^[A-Z]{3}$/, "ISO 4217 (uppercase, 3 chars)");

// New records must use makeRaceId() which produces a 16-char hex string.
// The schema accepts any non-empty string so that legacy UUIDs from the
// static korea.json fallback also parse. Pipelines that insert into D1 should
// still call makeRaceId() for canonical ids.
export const RaceSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  nameLocal: z.string(),
  date: z.string(),           // ISO 8601 (YYYY-MM-DD or full ISO). Use new Date(...).getTime() for arithmetic.
  region: z.string(),
  lat: z.number(),
  lng: z.number(),
  distances: z.array(DistanceSchema),
  status: RaceStatusSchema,
  registrationUrl: z.string().url().optional(),
  sourceUrl: z.string().url(),
  country: COUNTRY_CODE.default("KR"),
  currency: CURRENCY_CODE.default("KRW"),
  updatedAt: z.string(),

  // --- optional UI/legacy fields (not persisted in D1 Phase 1) ---
  // Present on korea.json fallback path; undefined for /api/races responses.
  countryName: z.string().optional(),
  city: z.string().optional(),
  venue: z.string().optional(),
  coordinate: CoordinateSchema.optional(),
  fees: z.array(FeeSchema).optional(),
  registration: RegistrationPeriodSchema.optional(),
  website: z.string().url().optional(),
});
export type Race = z.infer<typeof RaceSchema>;
